import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { useOfficialHomeContext } from '../../OfficialHomeContext';
import { IP_WORKFLOW_CONTENT, IP_WORKFLOW_VIDEOS } from './content';
import styles from './index.module.scss';

const MOBILE_LAYOUT_QUERY = '(max-width: 900px)';
const STATIC_LAYOUT_QUERY = '(max-width: 900px), (max-height: 650px), (prefers-reduced-motion: reduce)';

/** 视频临近视口才加载；离屏、后台和切换步骤时暂停，避免四个播放器同时解码。 */
function WorkflowVideo({ src, title, active }: { src: string; title: string; active: boolean; }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [loaded, setLoaded] = useState(false);
  const [visible, setVisible] = useState(false);

  // 预加载与播放阈值分离，给相邻卡片留出缓冲时间。
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;
    const preloadObserver = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setLoaded(true);
        preloadObserver.disconnect();
      }
    }, { rootMargin: '400px' });
    const visibilityObserver = new IntersectionObserver(([entry]) => {
      setVisible(entry.isIntersecting && entry.intersectionRatio >= 0.5);
    }, { threshold: 0.5 });
    preloadObserver.observe(video);
    visibilityObserver.observe(video);
    return () => {
      preloadObserver.disconnect();
      visibilityObserver.disconnect();
    };
  }, []);

  // 尊重减少动态效果设置；视频仅用于流程演示，不向用户透出原生播放控制栏。
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !loaded) return undefined;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    /** 统一处理标签页可见性与系统动态效果偏好。 */
    const syncPlayback = () => {
      if (active && visible && !document.hidden && !reducedMotion.matches) {
        video.play()?.catch(() => undefined);
      } else {
        video.pause();
      }
    };
    syncPlayback();
    document.addEventListener('visibilitychange', syncPlayback);
    reducedMotion.addEventListener('change', syncPlayback);
    return () => {
      video.pause();
      document.removeEventListener('visibilitychange', syncPlayback);
      reducedMotion.removeEventListener('change', syncPlayback);
    };
  }, [active, loaded, visible]);

  return (
    <video
      ref={videoRef}
      className={styles.video}
      crossOrigin="anonymous"
      src={loaded ? src : undefined}
      muted
      loop
      playsInline
      preload="metadata"
      controls={false}
      aria-label={title}
    />
  );
}

/** 官网四步 IP 创作流程；桌面跟随页面滚动，移动端通过横向吸附卡片同步步骤。 */
export default function IPWorkflow() {
  const context = useOfficialHomeContext();
  const steps = IP_WORKFLOW_CONTENT[context?.languageCode ?? 'en'];
  const [activeIndex, setActiveIndex] = useState(0);
  const sectionRef = useRef<HTMLElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const strideRef = useRef(600);

  // 只在动画帧中更新位移，步骤改变时才触发 React 渲染，不拦截滚轮或触摸事件。
  useEffect(() => {
    const section = sectionRef.current;
    const track = trackRef.current;
    if (!section || !track) return undefined;
    const staticLayout = window.matchMedia(STATIC_LAYOUT_QUERY);
    const mobileLayout = window.matchMedia(MOBILE_LAYOUT_QUERY);
    let frame = 0;
    /** 根据真实卡片高度计算轨道距离，兼容窗口缩放与不同屏幕宽度。 */
    const syncScroll = () => {
      frame = 0;
      const cards = Array.from(track.children);
      // H5 以横向轨道的中心卡片为选中项，点击和手势滑动共用同一个状态来源。
      if (mobileLayout.matches) {
        const trackRect = track.getBoundingClientRect();
        const center = trackRect.left + trackRect.width / 2;
        let closestIndex = 0;
        let distance = Infinity;
        cards.forEach((card, index) => {
          const rect = card.getBoundingClientRect();
          const nextDistance = Math.abs(rect.left + rect.width / 2 - center);
          if (nextDistance < distance) {
            distance = nextDistance;
            closestIndex = index;
          }
        });
        setActiveIndex(closestIndex);
        return;
      }
      if (staticLayout.matches) {
        let closestIndex = 0;
        let distance = Infinity;
        cards.forEach((card, index) => {
          const rect = card.getBoundingClientRect();
          const nextDistance = Math.abs(rect.top + rect.height / 2 - window.innerHeight / 2);
          if (nextDistance < distance) {
            distance = nextDistance;
            closestIndex = index;
          }
        });
        setActiveIndex(closestIndex);
        return;
      }
      const cardHeight = cards[0]?.getBoundingClientRect().height ?? 560;
      const stride = cardHeight + 40;
      strideRef.current = stride;
      const maxTravel = stride * (steps.length - 1);
      section.style.setProperty('--scroll-distance', `${maxTravel}px`);
      const travel = Math.min(maxTravel, Math.max(0, -section.getBoundingClientRect().top));
      track.style.transform = `translate3d(0, ${-cardHeight / 2 - travel}px, 0)`;
      setActiveIndex(Math.round(travel / stride));
    };
    /** 合并同一帧内的滚动与尺寸变化。 */
    const scheduleSync = () => {
      if (!frame) frame = window.requestAnimationFrame(syncScroll);
    };
    const resizeObserver = new ResizeObserver(scheduleSync);
    resizeObserver.observe(track);
    track.addEventListener('scroll', scheduleSync, { passive: true });
    window.addEventListener('scroll', scheduleSync, { passive: true });
    window.addEventListener('resize', scheduleSync);
    staticLayout.addEventListener('change', scheduleSync);
    syncScroll();
    return () => {
      window.cancelAnimationFrame(frame);
      resizeObserver.disconnect();
      track.removeEventListener('scroll', scheduleSync);
      window.removeEventListener('scroll', scheduleSync);
      window.removeEventListener('resize', scheduleSync);
      staticLayout.removeEventListener('change', scheduleSync);
    };
  }, [steps.length]);

  /** H5 点击仅横向居中对应卡片；桌面保持原有页面滚动定位。 */
  const selectStep = (index: number) => {
    const track = trackRef.current;
    if (window.matchMedia(MOBILE_LAYOUT_QUERY).matches && track) {
      const card = track.children[index];
      if (!card) return;
      const trackRect = track.getBoundingClientRect();
      const cardRect = card.getBoundingClientRect();
      track.scrollTo({
        left: track.scrollLeft + cardRect.left + cardRect.width / 2 - trackRect.left - trackRect.width / 2,
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      });
      return;
    }
    const section = sectionRef.current;
    if (!section) return;
    window.scrollTo({
      top: window.scrollY + section.getBoundingClientRect().top + index * strideRef.current,
      behavior: 'smooth',
    });
  };

  return (
    <section ref={sectionRef} className={styles.container} data-testid="ip-workflow">
      <div className={styles.stage}>
        <div className={styles.layout}>
          <ol className={styles.steps}>
            {steps.map((step, index) => (
              <li key={step.id} className={classNames(styles.step, { [styles.active]: index === activeIndex })}>
                <button
                  type="button"
                  className={styles.stepButton}
                  onClick={() => selectStep(index)}
                  aria-expanded={index === activeIndex}
                  aria-controls={`ip-workflow-description-${step.id}`}
                >
                  <span className={styles.number}>{index + 1}</span>
                  <span className={styles.title}>{step.title}</span>
                </button>
                <div id={`ip-workflow-description-${step.id}`} className={styles.description} hidden={index !== activeIndex}>
                  <p>{step.description}</p>
                </div>
              </li>
            ))}
          </ol>
          <div className={styles.mediaWindow}>
            <div ref={trackRef} className={styles.track}>
              {steps.map((step, index) => (
                <article key={step.id} className={classNames(styles.card, { [styles.currentCard]: index === activeIndex })}>
                  <div className={styles.mobileCopy}>
                    <h3>{`${index + 1}. ${step.title}`}</h3>
                    <p>{step.description}</p>
                  </div>
                  <div className={styles.media}>
                    <WorkflowVideo src={IP_WORKFLOW_VIDEOS[index]} title={step.title} active={index === activeIndex} />
                  </div>
                </article>
              ))}
            </div>
          </div>
        </div>
        <div className={styles.fadeTop} aria-hidden="true" />
        <div className={styles.fadeBottom} aria-hidden="true" />
      </div>
    </section>
  );
}
